import { useEffect, useState } from 'react';

import { fetchEventsPublic } from '../lib/events';
import { getFileUrl } from '../lib/pb';

export default function Agenda() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortMode, setSortMode] = useState('date'); // 'date' | 'title'
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const items = await fetchEventsPublic({ includePast: true });
        if (!cancelled) {
          setEvents(items || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Erreur lors du chargement des événements');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const now = new Date();

  const withDates = events.map(ev => {
    const start = ev.start_at ? new Date(ev.start_at) : null;
    const end = ev.end_at ? new Date(ev.end_at) : null;
    return { ...ev, _start: start, _end: end };
  });

  const upcoming = withDates
    .filter(ev => {
      if (!ev._start && !ev._end) return false;
      if (ev._end) return ev._end >= now;
      return ev._start && ev._start >= now;
    })
    .sort((a, b) => {
      if (sortMode === 'title') {
        return (a.title || '').localeCompare(b.title || '');
      }
      const da = a._start ? a._start.getTime() : 0;
      const db = b._start ? b._start.getTime() : 0;
      return da - db;
    });

  const past = withDates
    .filter(ev => !upcoming.includes(ev))
    .sort((a, b) => {
      if (sortMode === 'title') {
        return (a.title || '').localeCompare(b.title || '');
      }
      const da = a._start ? a._start.getTime() : 0;
      const db = b._start ? b._start.getTime() : 0;
      return db - da; // plus récents d'abord
    });

  return (
    <>
    <div className="agenda-page-signature">
      <div className="container-agenda-premium">
        
        {/* HEADER SECTION */}
        <header className="agenda-header-main">
           <div className="tag-line-premium">AEF 32 • AGENDA & ÉVÉNEMENTS</div>
           <h1>Agenda</h1>
           <p className="agenda-subtitle">
             Retrouvez nos prochains événements, démonstrations et rendez-vous AEF32.
           </p>
        </header>

        {/* FILTERS SECTION */}
        {!loading && !error && (upcoming.length || past.length) && (
          <div className="agenda-filters-bar">
            <div className="sort-pill-container">
              <span className="sort-label">Trier par</span>
              <div className="sort-toggles">
                <button 
                  className={`sort-btn ${sortMode === 'date' ? 'active' : ''}`}
                  onClick={() => setSortMode('date')}
                >
                  Date
                </button>
                <button 
                  className={`sort-btn ${sortMode === 'title' ? 'active' : ''}`}
                  onClick={() => setSortMode('title')}
                >
                  Titre
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="agenda-loading-state">
             <div className="spinner-signature"></div>
             <p>Chargement des événements…</p>
          </div>
        )}

        {error && !loading && (
          <div className="agenda-error-state">
             <p>{error}</p>
          </div>
        )}

        {!loading && !error && !upcoming.length && !past.length && (
          <div className="agenda-empty-state">
             <p>Aucun événement prévu pour le moment.</p>
          </div>
        )}

        {/* UPCOMING EVENTS */}
        {!loading && !error && !!upcoming.length && (
          <section className="events-section-premium">
            <div className="section-title-box">
               <div className="accent-bar"></div>
               <h2>À venir</h2>
            </div>
            <div className="events-grid-signature">
              {upcoming.map(event => {
                const date = event._start;
                const day = date ? date.getDate() : '';
                const month = date ? date.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : '';
                const year = date ? date.getFullYear() : '';
                
                return (
                  <article 
                    key={event.id} 
                    className="event-card-elite upcoming shadow-signature"
                    onClick={() => setSelected(event)}
                  >
                    <div className="event-visual-wrap">
                      {Array.isArray(event.images) && event.images[0] ? (
                        <img src={getFileUrl(event, event.images[0])} alt={event.title} />
                      ) : (
                        <div className="event-placeholder">📅</div>
                      )}
                      <div className="date-badge-glass">
                         <span className="d-day">{day}</span>
                         <span className="d-month">{month}</span>
                      </div>
                    </div>
                    <div className="event-info-box">
                      <div className="event-meta-top">
                         {event.location && (
                           <span className="loc-tag">
                             <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-tiny"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                             {event.location}
                           </span>
                         )}
                         <span className="year-tag">{year}</span>
                      </div>
                      <h3>{event.title}</h3>
                      {event.excerpt && <p className="excerpt-txt">{event.excerpt}</p>}
                      <div className="card-footer-cta">
                         En savoir plus <span className="arrow-small">→</span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* PAST EVENTS */}
        {!loading && !error && !!past.length && (
          <section className="events-section-premium past-events">
            <div className="section-title-box">
               <div className="accent-bar gray"></div>
               <h2>Événements passés</h2>
            </div>
            <div className="events-grid-signature small-grid">
              {past.map(event => {
                 const date = event._start;
                 const formatted = date ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                 
                 return (
                   <article 
                     key={event.id} 
                     className="event-card-compact shadow-signature"
                     onClick={() => setSelected(event)}
                   >
                     <div className="compact-visual">
                        {Array.isArray(event.images) && event.images[0] ? (
                          <img src={getFileUrl(event, event.images[0])} alt={event.title} />
                        ) : (
                          <div className="placeholder-mini">🗓️</div>
                        )}
                        <div className="status-tag">Passé</div>
                     </div>
                     <div className="compact-info">
                        <div className="compact-date">{formatted}</div>
                        <h4>{event.title}</h4>
                        {event.location && <div className="compact-loc">{event.location}</div>}
                     </div>
                   </article>
                 );
              })}
            </div>
          </section>
        )}

        {/* EVENT MODAL */}
        {selected && (
          <div className="signature-modal-overlay" onClick={() => setSelected(null)}>
            <div className="modal-content-signature animated-up" onClick={e => e.stopPropagation()}>
              <div className="modal-header-hero">
                {Array.isArray(selected.images) && selected.images[0] ? (
                  <img src={getFileUrl(selected, selected.images[0])} alt={selected.title} />
                ) : (
                  <div className="modal-hero-placeholder">📅</div>
                )}
                <button className="btn-close-modal" onClick={() => setSelected(null)}>×</button>
                <div className="modal-hero-labels">
                   <div className="modal-type-tag">ÉVÉNEMENT AEF32</div>
                   <h2>{selected.title}</h2>
                </div>
              </div>
              
              <div className="modal-body-premium">
                <div className="modal-quick-info">
                   <div className="info-item-box">
                      <div className="i-icon">📅</div>
                      <div className="i-data">
                         {selected._start && <span>Du {selected._start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
                         {selected._end && <span> au {selected._end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
                      </div>
                   </div>
                   {selected.location && (
                     <div className="info-item-box">
                        <div className="i-icon">📍</div>
                        <div className="i-data">{selected.location}</div>
                     </div>
                   )}
                </div>

                <div className="modal-main-text">
                   {selected.excerpt && <p className="modal-excerpt">{selected.excerpt}</p>}
                   {selected.description && <div className="modal-description">{selected.description}</div>}
                </div>

                <div className="modal-actions-footer">
                   <button className="btn-close-final" onClick={() => setSelected(null)}>Fermer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .agenda-page-signature { 
             background: #fff; padding: 40px 0; min-height: 100vh;
             font-family: 'Inter', sans-serif; color: #0f172a; 
          }
          .container-agenda-premium { max-width: 1100px; margin: 0 auto; padding: 0 20px; }

          /* HEADER */
          .agenda-header-main { text-align: center; margin-bottom: 60px; }
          .tag-line-premium { font-size: 11px; font-weight: 800; color: var(--accent); letter-spacing: 2.5px; margin-bottom: 15px; }
          .agenda-header-main h1 { font-size: clamp(32px, 5vw, 48px); font-weight: 900; margin: 0 0 15px 0; letter-spacing: -1px; }
          .agenda-subtitle { color: #64748b; font-size: 17px; max-width: 600px; margin: 0 auto; line-height: 1.6; }

          /* FILTERS */
          .agenda-filters-bar { display: flex; justify-content: center; margin-bottom: 50px; }
          .sort-pill-container { 
             display: flex; align-items: center; gap: 15px; background: #f8fafc; 
             padding: 6px 6px 6px 20px; border-radius: 999px; border: 1px solid #f1f5f9;
          }
          .sort-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; }
          .sort-toggles { display: flex; gap: 6px; }
          .sort-btn { 
            padding: 8px 20px; border-radius: 999px; border: none; background: transparent; 
            font-size: 13px; font-weight: 700; color: #64748b; cursor: pointer; transition: all 0.2s;
          }
          .sort-btn.active { background: #fff; color: var(--accent); box-shadow: 0 4px 10px rgba(0,0,0,0.05); }

          /* GRID & SECTIONS */
          .events-section-premium { margin-bottom: 80px; }
          .section-title-box { display: flex; align-items: center; gap: 15px; margin-bottom: 30px; }
          .accent-bar { width: 5px; height: 32px; background: var(--accent); border-radius: 4px; }
          .accent-bar.gray { background: #e2e8f0; }
          .section-title-box h2 { font-size: 24px; font-weight: 900; margin: 0; }

          .events-grid-signature { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 30px; }
          .events-grid-signature.small-grid { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; }

          /* EVENT CARD ELITE */
          .event-card-elite { 
             background: #fff; border-radius: 28px; border: 1px solid #f1f5f9; 
             overflow: hidden; cursor: pointer; transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
             display: flex; flex-direction: column;
          }
          .event-card-elite:hover { transform: translateY(-10px); border-color: var(--accent); box-shadow: 0 25px 50px rgba(15, 23, 42, 0.08); }
          .event-visual-wrap { height: 200px; position: relative; overflow: hidden; }
          .event-visual-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
          .event-card-elite:hover .event-visual-wrap img { transform: scale(1.08); }
          
          .date-badge-glass { 
             position: absolute; top: 15px; right: 15px; background: rgba(255, 255, 255, 0.7); 
             backdrop-filter: blur(12px); border: 1px solid rgba(255, 255, 255, 0.3);
             padding: 10px 15px; border-radius: 18px; display: flex; flex-direction: column; align-items: center;
             color: #0f172a; box-shadow: 0 8px 20px rgba(0,0,0,0.1);
          }
          .d-day { font-size: 22px; font-weight: 950; line-height: 1; }
          .d-month { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: var(--accent); }

          .event-info-box { padding: 25px; display: flex; flex-direction: column; flex-grow: 1; }
          .event-meta-top { display: flex; justify-content: space-between; margin-bottom: 12px; }
          .loc-tag { font-size: 12px; font-weight: 700; color: #64748b; display: flex; align-items: center; gap: 6px; }
          .year-tag { font-size: 12px; font-weight: 800; color: #e2e8f0; }
          .icon-tiny { width: 14px; height: 14px; }
          
          .event-info-box h3 { font-size: 19px; font-weight: 900; margin: 0 0 10px 0; line-height: 1.3; }
          .excerpt-txt { font-size: 14px; color: #475569; line-height: 1.6; margin-bottom: 20px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
          .card-footer-cta { margin-top: auto; font-size: 13px; font-weight: 800; color: var(--accent); display: flex; align-items: center; gap: 8px; }
          .arrow-small { transition: transform 0.2s ease; }
          .event-card-elite:hover .arrow-small { transform: translateX(5px); }

          /* COMPACT CARD (PAST) */
          .event-card-compact { 
             background: #fcfcfd; border-radius: 20px; border: 1px solid #f1f5f9; 
             padding: 12px; display: flex; gap: 15px; align-items: center; cursor: pointer;
             transition: all 0.3s;
          }
          .event-card-compact:hover { background: #fff; transform: scale(1.02); box-shadow: 0 10px 20px rgba(0,0,0,0.03); }
          .compact-visual { width: 80px; height: 80px; border-radius: 12px; overflow: hidden; position: relative; flex-shrink: 0; }
          .compact-visual img { width: 100%; height: 100%; object-fit: cover; filter: grayscale(0.8); }
          .status-tag { 
             position: absolute; bottom: 0; left: 0; right: 0; background: rgba(15, 23, 42, 0.6); 
             color: #fff; font-size: 9px; font-weight: 800; text-transform: uppercase; text-align: center; padding: 2px 0;
          }
          .compact-info h4 { font-size: 15px; font-weight: 850; margin: 0 0 4px 0; color: #334155; }
          .compact-date { font-size: 11px; font-weight: 800; color: var(--accent); margin-bottom: 2px; }
          .compact-loc { font-size: 12px; color: #94a3b8; }

          /* MODAL SIGNATURE */
          .signature-modal-overlay { 
             position: fixed; inset: 0; z-index: 100; background: rgba(15, 23, 42, 0.4); 
             backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 20px;
          }
          .modal-content-signature { 
             background: #fff; width: 100%; max-width: 800px; border-radius: 32px; 
             max-height: 90vh; overflow: hidden; position: relative;
             box-shadow: 0 40px 80px -20px rgba(0,0,0,0.3);
          }
          .animated-up { animation: modalUp 0.5s cubic-bezier(0.165, 0.84, 0.44, 1); }
          @keyframes modalUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

          .modal-header-hero { height: 350px; position: relative; }
          .modal-header-hero img { width: 100%; height: 100%; object-fit: cover; }
          .modal-hero-placeholder { width: 100%; height: 100%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; font-size: 60px; }
          .btn-close-modal { 
             position: absolute; top: 20px; right: 20px; width: 40px; height: 40px; 
             border-radius: 50%; border: none; background: rgba(255,255,255,0.9); 
             font-size: 24px; cursor: pointer; display: flex; align-items: center; justify-content: center;
             box-shadow: 0 4px 15px rgba(0,0,0,0.1); transition: all 0.2s;
          }
          .btn-close-modal:hover { background: #fff; transform: rotate(90deg); }
          
          .modal-hero-labels { 
             position: absolute; bottom: 0; left: 0; right: 0; padding: 40px; 
             background: linear-gradient(to top, rgba(0,0,0,0.8), transparent); color: #fff;
          }
          .modal-type-tag { font-size: 11px; font-weight: 800; letter-spacing: 2px; color: var(--accent); margin-bottom: 10px; }
          .modal-hero-labels h2 { font-size: 32px; font-weight: 900; margin: 0; }

          .modal-body-premium { padding: 40px; overflow-y: auto; max-height: calc(90vh - 350px); }
          .modal-quick-info { display: flex; gap: 30px; margin-bottom: 40px; flex-wrap: wrap; }
          .info-item-box { display: flex; align-items: center; gap: 15px; }
          .i-icon { width: 48px; height: 48px; background: #f8fafc; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
          .i-data { font-size: 14px; font-weight: 700; color: #475569; }

          .modal-excerpt { font-size: 18px; font-weight: 600; color: #1e293b; line-height: 1.6; margin-bottom: 25px; }
          .modal-description { font-size: 15px; color: #475569; line-height: 1.8; white-space: pre-wrap; }

          .modal-actions-footer { margin-top: 40px; padding-top: 30px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; }
          .btn-close-final { 
             background: #0f172a; color: #fff; border: none; padding: 12px 30px; 
             border-radius: 12px; font-weight: 800; cursor: pointer; transition: all 0.2s;
          }
          .btn-close-final:hover { background: #1e293b; transform: translateY(-2px); }

          @media (max-width: 640px) {
             .modal-header-hero { height: 220px; }
             .modal-hero-labels { padding: 20px; }
             .modal-hero-labels h2 { font-size: 22px; }
             .modal-body-premium { padding: 25px; }
             .events-grid-signature { grid-template-columns: 1fr; }
          }
        `}</style>
      </div>
    </div>
    </>
  );
}
